package com.arenaproplay.admin.ui.theme

import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.styleModifier
import com.arenaproplay.admin.ui.utils.sFontSize
import com.arenaproplay.admin.ui.utils.sLetterSpacing
import com.arenaproplay.admin.ui.utils.sLineHeight
import org.jetbrains.compose.web.css.px

object AppTypography {

    const val PRIMARY_FONT_NAME = "Inter"
    const val PRIMARY_FONT_FALLBACK = "sans-serif"

    /** Night Arena heading / numeric display font. */
    const val HEADING_FONT_NAME = "Unbounded"
    const val HEADING_FONT_FALLBACK = "sans-serif"

    const val DISPLAY_FONT_NAME = "Unbounded"
    const val DISPLAY_FONT_FALLBACK = "sans-serif"

    const val SECONDARY_FONT_NAME = "Inter"
    const val SECONDARY_FONT_FALLBACK = "sans-serif"

    const val CARD_HEADLINE_FONT = "Unbounded"
    val CARD_HEADLINE_FALLBACKS = arrayOf("Inter", "sans-serif")

    const val CARD_TEXT_FONT = "Inter"
    const val CARD_TEXT_FALLBACK = "sans-serif"

    const val MONO_FONT_NAME = "JetBrains Mono"
    const val MONO_FONT_FALLBACK_1 = "ui-monospace"
    const val MONO_FONT_FALLBACK_2 = "monospace"
    const val MONO_FONT_FALLBACK = "monospace"

    val Light = 300
    val Regular = 400
    val Medium = 500
    val SemiBold = 600
    val Bold = 700

    val H1 = 48.px
    val H2 = 36.px
    val H3 = 28.px
    val H4 = 24.px
    val H5 = 20.px
    val H6 = 18.px
    val Body = 16.px
    val BodySmall = 14.px
    val Caption = 12.px
    val Overline = 10.px

    val Tight = 1.2
    val Normal = 1.5
    val Relaxed = 1.75
}

fun Modifier.h0(): Modifier = this
    .sFontSize(46)
    .fontWeight(AppTypography.SemiBold)
    .sLineHeight(46)
    .sLetterSpacing(1)
    .fontFamily(AppTypography.PRIMARY_FONT_NAME, AppTypography.PRIMARY_FONT_FALLBACK)

fun Modifier.h1(): Modifier = this
    .sFontSize(34)
    .fontWeight(AppTypography.SemiBold)
    .sLineHeight(40)
    .sLetterSpacing(1)
    .fontFamily(AppTypography.PRIMARY_FONT_NAME, AppTypography.PRIMARY_FONT_FALLBACK)

fun Modifier.h2(): Modifier = this
    .sFontSize(26)
    .fontWeight(AppTypography.SemiBold)
    .sLineHeight(30)
    .sLetterSpacing(1)
    .fontFamily(AppTypography.PRIMARY_FONT_NAME, AppTypography.PRIMARY_FONT_FALLBACK)

fun Modifier.h2Medium(): Modifier = this
    .sFontSize(24)
    .fontWeight(AppTypography.Medium)
    .sLineHeight(32)
    .sLetterSpacing(1)
    .fontFamily(AppTypography.PRIMARY_FONT_NAME, AppTypography.PRIMARY_FONT_FALLBACK)

fun Modifier.h3(): Modifier = this
    .sFontSize(20)
    .fontWeight(AppTypography.Medium)
    .sLineHeight(24)
    .sLetterSpacing(1)
    .fontFamily(AppTypography.PRIMARY_FONT_NAME, AppTypography.PRIMARY_FONT_FALLBACK)

fun Modifier.h4(): Modifier = h2Medium()

fun Modifier.body3xlSemiBold(): Modifier = this
    .sFontSize(36)
    .fontWeight(AppTypography.SemiBold)
    .sLineHeight(42)
    .sLetterSpacing(1)
    .fontFamily(AppTypography.PRIMARY_FONT_NAME, AppTypography.PRIMARY_FONT_FALLBACK)

fun Modifier.body2xlSemiBold(): Modifier = this
    .sFontSize(22)
    .fontWeight(AppTypography.SemiBold)
    .sLineHeight(26)
    .sLetterSpacing(1)
    .fontFamily(AppTypography.PRIMARY_FONT_NAME, AppTypography.PRIMARY_FONT_FALLBACK)

fun Modifier.body2xlMedium(): Modifier = this
    .sFontSize(22)
    .fontWeight(AppTypography.Medium)
    .sLineHeight(26)
    .sLetterSpacing(1)
    .fontFamily(AppTypography.PRIMARY_FONT_NAME, AppTypography.PRIMARY_FONT_FALLBACK)

fun Modifier.body2xlRegular(): Modifier = this
    .sFontSize(22)
    .fontWeight(AppTypography.Regular)
    .sLineHeight(26)
    .sLetterSpacing(1)
    .fontFamily(AppTypography.PRIMARY_FONT_NAME, AppTypography.PRIMARY_FONT_FALLBACK)

fun Modifier.bodyXlMedium(): Modifier = this
    .sFontSize(18)
    .fontWeight(AppTypography.Medium)
    .sLineHeight(22)
    .sLetterSpacing(0.5)
    .fontFamily(AppTypography.PRIMARY_FONT_NAME, AppTypography.PRIMARY_FONT_FALLBACK)

fun Modifier.bodyXlSemiBold(): Modifier = this
    .sFontSize(18)
    .fontWeight(AppTypography.SemiBold)
    .sLineHeight(22)
    .sLetterSpacing(0.5)
    .fontFamily(AppTypography.PRIMARY_FONT_NAME, AppTypography.PRIMARY_FONT_FALLBACK)

fun Modifier.bodyXlBold(): Modifier = this
    .sFontSize(18)
    .fontWeight(AppTypography.Bold)
    .sLineHeight(22)
    .sLetterSpacing(0.5)
    .fontFamily(AppTypography.PRIMARY_FONT_NAME, AppTypography.PRIMARY_FONT_FALLBACK)

fun Modifier.bodyXlRegular(): Modifier = this
    .sFontSize(18)
    .fontWeight(AppTypography.Regular)
    .sLineHeight(26)
    .sLetterSpacing(0.5)
    .fontFamily(AppTypography.PRIMARY_FONT_NAME, AppTypography.PRIMARY_FONT_FALLBACK)

fun Modifier.bodyLMedium(): Modifier = this
    .sFontSize(16)
    .fontWeight(AppTypography.Medium)
    .sLineHeight(22)
    .sLetterSpacing(0.2)
    .fontFamily(AppTypography.PRIMARY_FONT_NAME, AppTypography.PRIMARY_FONT_FALLBACK)

fun Modifier.bodyLSemiBold(): Modifier = this
    .sFontSize(16)
    .fontWeight(AppTypography.SemiBold)
    .sLineHeight(22)
    .sLetterSpacing(0.2)
    .fontFamily(AppTypography.PRIMARY_FONT_NAME, AppTypography.PRIMARY_FONT_FALLBACK)

fun Modifier.bodyLRegular(): Modifier = this
    .sFontSize(16)
    .fontWeight(AppTypography.Regular)
    .sLineHeight(22)
    .sLetterSpacing(0.2)
    .fontFamily(AppTypography.PRIMARY_FONT_NAME, AppTypography.PRIMARY_FONT_FALLBACK)

fun Modifier.bodyLLight(): Modifier = this
    .sFontSize(16)
    .fontWeight(AppTypography.Light)
    .sLineHeight(22)
    .sLetterSpacing(0.2)
    .fontFamily(AppTypography.PRIMARY_FONT_NAME, AppTypography.PRIMARY_FONT_FALLBACK)

fun Modifier.bodyMRegular(): Modifier = this
    .sFontSize(14)
    .fontWeight(AppTypography.Regular)
    .sLineHeight(20)
    .sLetterSpacing(0.2)
    .fontFamily(AppTypography.PRIMARY_FONT_NAME, AppTypography.PRIMARY_FONT_FALLBACK)

fun Modifier.bodyMMedium(): Modifier = this
    .sFontSize(14)
    .fontWeight(AppTypography.Medium)
    .sLineHeight(20)
    .sLetterSpacing(0.2)
    .fontFamily(AppTypography.PRIMARY_FONT_NAME, AppTypography.PRIMARY_FONT_FALLBACK)

fun Modifier.bodyMLight(): Modifier = this
    .sFontSize(14)
    .fontWeight(AppTypography.Light)
    .sLineHeight(20)
    .sLetterSpacing(0.2)
    .fontFamily(AppTypography.PRIMARY_FONT_NAME, AppTypography.PRIMARY_FONT_FALLBACK)

fun Modifier.bodySRegular(): Modifier = this
    .sFontSize(13)
    .fontWeight(AppTypography.Regular)
    .sLineHeight(18)
    .sLetterSpacing(0.1)
    .fontFamily(AppTypography.PRIMARY_FONT_NAME, AppTypography.PRIMARY_FONT_FALLBACK)

fun Modifier.bodySMedium(): Modifier = this
    .sFontSize(13)
    .fontWeight(AppTypography.Medium)
    .sLineHeight(18)
    .sLetterSpacing(0.1)
    .fontFamily(AppTypography.PRIMARY_FONT_NAME, AppTypography.PRIMARY_FONT_FALLBACK)

fun Modifier.bodySBold(): Modifier = this
    .sFontSize(13)
    .fontWeight(AppTypography.Bold)
    .sLineHeight(18)
    .sLetterSpacing(0.1)
    .fontFamily(AppTypography.CARD_HEADLINE_FONT, *AppTypography.CARD_HEADLINE_FALLBACKS)

fun Modifier.mono(size: Int = 14): Modifier = this
    .sFontSize(size)
    .fontWeight(AppTypography.Medium)
    .fontFamily(AppTypography.MONO_FONT_NAME, AppTypography.MONO_FONT_FALLBACK_1, AppTypography.MONO_FONT_FALLBACK_2)
    .styleModifier {
        property("font-feature-settings", "\"tnum\" 1, \"ss01\" 1")
        property("font-variant-numeric", "tabular-nums")
        property("letter-spacing", "-0.01em")
    }

/** Hero numeric value — Unbounded display, tight, tabular nums. */
fun Modifier.numHero(): Modifier = this
    .sFontSize(44)
    .fontWeight(AppTypography.Bold)
    .sLineHeight(48)
    .fontFamily(AppTypography.DISPLAY_FONT_NAME, AppTypography.PRIMARY_FONT_NAME, AppTypography.DISPLAY_FONT_FALLBACK)
    .styleModifier {
        property("font-feature-settings", "\"tnum\" 1")
        property("font-variant-numeric", "tabular-nums")
        property("letter-spacing", "-0.02em")
    }

fun Modifier.numLarge(): Modifier = this
    .sFontSize(30)
    .fontWeight(AppTypography.SemiBold)
    .sLineHeight(34)
    .fontFamily(AppTypography.DISPLAY_FONT_NAME, AppTypography.PRIMARY_FONT_NAME, AppTypography.DISPLAY_FONT_FALLBACK)
    .styleModifier {
        property("font-feature-settings", "\"tnum\" 1")
        property("font-variant-numeric", "tabular-nums")
        property("letter-spacing", "-0.02em")
    }

fun Modifier.numMedium(): Modifier = this
    .sFontSize(19)
    .fontWeight(AppTypography.SemiBold)
    .sLineHeight(22)
    .fontFamily(AppTypography.DISPLAY_FONT_NAME, AppTypography.PRIMARY_FONT_NAME, AppTypography.DISPLAY_FONT_FALLBACK)
    .styleModifier {
        property("font-feature-settings", "\"tnum\" 1")
        property("font-variant-numeric", "tabular-nums")
        property("letter-spacing", "-0.02em")
    }

fun Modifier.numSmall(): Modifier = this
    .sFontSize(14)
    .fontWeight(AppTypography.Medium)
    .sLineHeight(18)
    .fontFamily(AppTypography.MONO_FONT_NAME, AppTypography.MONO_FONT_FALLBACK_1, AppTypography.MONO_FONT_FALLBACK_2)
    .styleModifier {
        property("font-feature-settings", "\"tnum\" 1, \"ss01\" 1")
        property("font-variant-numeric", "tabular-nums")
        property("letter-spacing", "0")
    }

fun Modifier.displayHero(): Modifier = this
    .sFontSize(44)
    .fontWeight(AppTypography.Bold)
    .sLineHeight(48)
    .fontFamily(AppTypography.DISPLAY_FONT_NAME, AppTypography.PRIMARY_FONT_NAME, AppTypography.PRIMARY_FONT_FALLBACK)
    .styleModifier {
        property("letter-spacing", "-0.025em")
    }

fun Modifier.displayLarge(): Modifier = this
    .sFontSize(30)
    .fontWeight(AppTypography.SemiBold)
    .sLineHeight(36)
    .fontFamily(AppTypography.DISPLAY_FONT_NAME, AppTypography.PRIMARY_FONT_NAME, AppTypography.PRIMARY_FONT_FALLBACK)
    .styleModifier {
        property("letter-spacing", "-0.022em")
    }

fun Modifier.displayMedium(): Modifier = this
    .sFontSize(21)
    .fontWeight(AppTypography.SemiBold)
    .sLineHeight(26)
    .fontFamily(AppTypography.DISPLAY_FONT_NAME, AppTypography.PRIMARY_FONT_NAME, AppTypography.PRIMARY_FONT_FALLBACK)
    .styleModifier {
        property("letter-spacing", "-0.015em")
    }

fun Modifier.displaySmall(): Modifier = this
    .sFontSize(16)
    .fontWeight(AppTypography.Medium)
    .sLineHeight(22)
    .fontFamily(AppTypography.DISPLAY_FONT_NAME, AppTypography.PRIMARY_FONT_NAME, AppTypography.PRIMARY_FONT_FALLBACK)
    .styleModifier {
        property("letter-spacing", "-0.01em")
    }

/** Eyebrow/caption marker — uppercase, wide tracking, mono. */
fun Modifier.eyebrow(): Modifier = this
    .sFontSize(11)
    .fontWeight(AppTypography.Medium)
    .sLineHeight(14)
    .fontFamily(AppTypography.MONO_FONT_NAME, AppTypography.MONO_FONT_FALLBACK_1, AppTypography.MONO_FONT_FALLBACK_2)
    .styleModifier {
        property("text-transform", "uppercase")
        property("letter-spacing", "0.18em")
        property("font-feature-settings", "\"ss01\" 1")
    }

fun Modifier.eyebrowLarge(): Modifier = this
    .sFontSize(12)
    .fontWeight(AppTypography.Medium)
    .sLineHeight(16)
    .fontFamily(AppTypography.MONO_FONT_NAME, AppTypography.MONO_FONT_FALLBACK_1, AppTypography.MONO_FONT_FALLBACK_2)
    .styleModifier {
        property("text-transform", "uppercase")
        property("letter-spacing", "0.20em")
    }

fun Modifier.tabularNums(): Modifier = styleModifier {
    property("font-feature-settings", "\"tnum\" 1")
    property("font-variant-numeric", "tabular-nums")
}

fun Modifier.body(): Modifier = bodyLRegular()
fun Modifier.bodySmall(): Modifier = bodyMRegular()
fun Modifier.caption(): Modifier = bodySRegular()
